import { PersonAddressSchema } from "src/schemas/Address";
import { isNotNull } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import {
  ADDRESS_FIELD_TYPES,
  NAME_FIELD_TYPES,
  ParsedAnalyzeIdResult,
  Polygon,
} from "./analyzeIdUtils";

export enum ProfileType {
  Student = "student",
  Parent = "parent",
  Sibling = "sibling",
}
export type Profile = {
  id: string;
  person_type: ProfileType;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  birth_date?: string | null;
  street_address?: string | null;
  street_address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  email_address?: string | null;
  phone_number?: string | null;
  sms_okay?: boolean | null;
  email_okay?: boolean | null;
  user?: {
    name?: string | null;
  } | null;
  applicant_attending_schools?:
    | {
        description?: string | null;
      }[]
    | null;
};

export enum IdentityAnalyzeIdMatchType {
  Question = "question",
  Profile = "profile",
}
export enum ProfileFieldType {
  Name = "name",
  DateOfBirth = "date_of_birth",
  Address = "address",
}
export type IdentityAnalyzeIdMatch = {
  id: string;
  matchType: IdentityAnalyzeIdMatchType;
  profileFieldType?: ProfileFieldType;
  highlightAnalyzeIdFieldTypes: string[];
  documentMatchText: string;
  matchHighlights: Polygon[];
};

// Concatenate strings and normalize them (lowercase, collapse spaces, alphanumeric only)
function normalizeStrings(strings: string[]): string {
  return strings
    .join()
    .toLowerCase()
    .replace(/[^a-z0-9]/gi, "");
}

function matchStrings(
  formFieldValues: string[],
  idFieldValues: string[]
): boolean {
  const formText = normalizeStrings(formFieldValues);
  const idText = normalizeStrings(idFieldValues);
  return formText.length > 0 && formText === idText;
}

/**
 * Compare verification answers to AnalyzeID response.
 *
 * Currently considering matching as follows:
 * - FreeTextType > ID name fields
 * - AddressType > ID address fields
 * - DateType > ID date of birth field
 */
export function matchAnswersToAnalyzeId(
  questions: AF.Question<AF.WithId>[],
  answers: Record<string, any>,
  analyzeIdResults: ParsedAnalyzeIdResult[]
): IdentityAnalyzeIdMatch[] {
  const rows: IdentityAnalyzeIdMatch[] = [];

  const consideredQuestions = questions.filter((q) =>
    [AF.FreeTextType, AF.AddressType, AF.DateType].includes(q.type)
  );

  for (const q of consideredQuestions) {
    const answer = answers[q.id];
    let matched = false;
    let highlightAnalyzeIdFieldTypes: string[] = [];
    let documentMatchText = "";
    const matchHighlights: Polygon[] = [];

    switch (q.type) {
      case AF.FreeTextType: {
        const formFieldValues = typeof answer === "string" ? [answer] : [];
        const idFields = NAME_FIELD_TYPES.map((t) =>
          analyzeIdResults.find((r) => r.type === t)
        );
        const idFieldValues = idFields.map((f) => f?.value ?? "");
        matched = matchStrings(formFieldValues, idFieldValues);
        if (matched) {
          highlightAnalyzeIdFieldTypes = [...NAME_FIELD_TYPES];
          documentMatchText = idFieldValues.filter((part) => !!part).join(" ");
          matchHighlights.push(
            ...idFields.map((f) => f?.polygon).filter(isNotNull)
          );
        }
        break;
      }
      case AF.DateType: {
        const formDate = typeof answer === "string" ? answer : null; // always formatted yyyy-MM-dd
        const idDobField = analyzeIdResults.find(
          (r) => r.type === "DATE_OF_BIRTH"
        );
        const idDobValue = idDobField?.normalizedValue; // always formatted yyyy-MM-ddT00:00:00.000Z
        const idDob = idDobValue ? idDobValue.split("T")[0] : null;
        matched = Boolean(formDate && idDob && formDate === idDob);
        if (matched) {
          highlightAnalyzeIdFieldTypes = ["DATE_OF_BIRTH"];
          documentMatchText = idDob ?? "";
          idDobField?.polygon && matchHighlights.push(idDobField.polygon);
        }
        break;
      }
      case AF.AddressType: {
        const formAddressParse = PersonAddressSchema.safeParse(answer);
        if (!formAddressParse.success) {
          matched = false;
          break;
        }
        const formAddress = formAddressParse.data;
        const formAddressFieldValues = [
          formAddress.street_address,
          formAddress.street_address_line_2,
          formAddress.city,
          formAddress.state,
          formAddress.zip_code,
        ];
        const idAddressFields = ADDRESS_FIELD_TYPES.map((t) =>
          analyzeIdResults.find((r) => r.type === t)
        );
        const idAddressFieldMap = {
          address: idAddressFields[0]?.value ?? "",
          cityInAddress: idAddressFields[1]?.value ?? "",
          stateInAddress: idAddressFields[2]?.value ?? "",
          zipCodeInAddress: idAddressFields[3]?.value?.slice(0, 5) ?? "",
        };
        const idAddressFieldValues = Object.values(idAddressFieldMap);
        matched = matchStrings(formAddressFieldValues, idAddressFieldValues);
        if (matched) {
          highlightAnalyzeIdFieldTypes = [...ADDRESS_FIELD_TYPES];
          documentMatchText = `${idAddressFieldMap.address ?? ""}, ${
            idAddressFieldMap.cityInAddress ?? ""
          }, ${idAddressFieldMap.stateInAddress ?? ""} ${
            idAddressFieldMap.zipCodeInAddress ?? ""
          }`;
          matchHighlights.push(
            ...idAddressFields.map((f) => f?.polygon).filter(isNotNull)
          );
        }
        break;
      }
    }
    if (matched) {
      rows.push({
        id: q.id,
        matchType: IdentityAnalyzeIdMatchType.Question,
        highlightAnalyzeIdFieldTypes,
        documentMatchText,
        matchHighlights,
      });
    }
  }

  return rows;
}

export function matchProfileToAnalyzeId(
  profiles: Profile[],
  analyzeIdResults: ParsedAnalyzeIdResult[]
): IdentityAnalyzeIdMatch[] {
  const rows: IdentityAnalyzeIdMatch[] = [];

  for (const profile of profiles) {
    // name
    const profileNameFieldValues = [
      profile.first_name ?? "",
      profile.middle_name ?? "",
      profile.last_name ?? "",
    ];
    const idNameFields = NAME_FIELD_TYPES.map((t) =>
      analyzeIdResults.find((r) => r.type === t)
    );
    const idNameFieldValues = idNameFields.map((f) => f?.value ?? "");
    if (matchStrings(profileNameFieldValues, idNameFieldValues)) {
      rows.push({
        id: profile.id,
        matchType: IdentityAnalyzeIdMatchType.Profile,
        profileFieldType: ProfileFieldType.Name,
        highlightAnalyzeIdFieldTypes: [...NAME_FIELD_TYPES],
        documentMatchText: idNameFieldValues.filter((part) => !!part).join(" "),
        matchHighlights: idNameFields.map((f) => f?.polygon).filter(isNotNull),
      });
    }

    // date of birth
    const profileDob = profile.birth_date; // always formatted yyyy-MM-dd
    const idDobField = analyzeIdResults.find((r) => r.type === "DATE_OF_BIRTH");
    const idDobValue = idDobField?.normalizedValue; // always formatted yyyy-MM-ddT00:00:00.000Z
    const idDob = idDobValue ? idDobValue.split("T")[0] : null;
    if (profileDob && idDob && profileDob === idDob) {
      rows.push({
        id: profile.id,
        matchType: IdentityAnalyzeIdMatchType.Profile,
        profileFieldType: ProfileFieldType.DateOfBirth,
        highlightAnalyzeIdFieldTypes: ["DATE_OF_BIRTH"],
        documentMatchText: idDob ?? "",
        matchHighlights: idDobField?.polygon ? [idDobField.polygon] : [],
      });
    }

    // address
    if (profile.person_type === ProfileType.Sibling) {
      continue; // skip sibling addresses
    }
    const profileAddressFieldValues = [
      profile.street_address ?? "",
      profile.street_address_line_2 ?? "",
      profile.city ?? "",
      profile.state ?? "",
      profile.zip_code ?? "",
    ];
    const idAddressFields = ADDRESS_FIELD_TYPES.map((t) =>
      analyzeIdResults.find((r) => r.type === t)
    );
    const idAddressFieldMap = {
      address: idAddressFields[0]?.value ?? "",
      cityInAddress: idAddressFields[1]?.value ?? "",
      stateInAddress: idAddressFields[2]?.value ?? "",
      zipCodeInAddress: idAddressFields[3]?.value?.slice(0, 5) ?? "",
    };
    const idAddressFieldValues = Object.values(idAddressFieldMap);
    if (matchStrings(profileAddressFieldValues, idAddressFieldValues)) {
      rows.push({
        id: profile.id,
        matchType: IdentityAnalyzeIdMatchType.Profile,
        profileFieldType: ProfileFieldType.Address,
        highlightAnalyzeIdFieldTypes: [...ADDRESS_FIELD_TYPES],
        documentMatchText: `${idAddressFieldMap.address ?? ""}, ${
          idAddressFieldMap.cityInAddress ?? ""
        }, ${idAddressFieldMap.stateInAddress ?? ""} ${
          idAddressFieldMap.zipCodeInAddress ?? ""
        }`,
        matchHighlights: idAddressFields
          .map((f) => f?.polygon)
          .filter(isNotNull),
      });
    }
  }

  return rows;
}
